import type { MorrowAccountType } from "../../onboardingStore";

/**
 * First-run wizard steps. Individual is the short path; company adds org + invite.
 * Progress numbering always uses this full path — never the pending-only subset —
 * so "step 3 of 4" stays 3 of 4 after earlier steps are skipped or already done.
 */
export type OnboardingStep = "accountType" | "company" | "profile" | "connect" | "invite";

export const INDIVIDUAL_ONBOARDING_STEPS: readonly OnboardingStep[] = [
  "accountType",
  "profile",
  "connect",
];

export const COMPANY_ONBOARDING_STEPS: readonly OnboardingStep[] = [
  "accountType",
  "company",
  "profile",
  "connect",
  "invite",
];

export function stepsForAccountType(
  accountType: MorrowAccountType | null,
): readonly OnboardingStep[] {
  return accountType === "company" ? COMPANY_ONBOARDING_STEPS : INDIVIDUAL_ONBOARDING_STEPS;
}

export type OnboardingPendingInput = {
  readonly detectedCompany: boolean;
  readonly questionnaireAnswered: boolean;
  readonly connectedCount: number;
  readonly membersCount: number;
  /**
   * True when the user joined an organization they did NOT create (an invited
   * member, i.e. an org is active and their role is not `org:admin`). Invited
   * members are shortcut past the org-setup + invite steps straight to the
   * personal steps — they shouldn't set up the company or invite others.
   */
  readonly isInvitedMember: boolean;
};

export function isOnboardingStepPending(
  step: OnboardingStep,
  input: OnboardingPendingInput,
): boolean {
  switch (step) {
    case "accountType":
    case "company":
      return !input.detectedCompany;
    case "profile":
      return !input.questionnaireAnswered;
    case "connect":
      // The tools step is always SHOWN in first-run onboarding (form → tools → profiling),
      // but it never BLOCKS: the user can Skip it and finish with nothing connected (apps
      // can be added later from settings). We deliberately don't hard-gate onboarding on a
      // connection — no step should stop someone from getting into the app.
      return true;
    case "invite":
      // Owners invite teammates once (until someone else is in the org); invited
      // members never see this step — they were the ones invited.
      return !input.isInvitedMember && input.membersCount <= 1;
  }
}

export function pendingOnboardingSteps(
  accountType: MorrowAccountType | null,
  input: OnboardingPendingInput,
): OnboardingStep[] {
  return stepsForAccountType(accountType).filter((step) => isOnboardingStepPending(step, input));
}

/**
 * The step "Next" should advance to: the first still-pending step that comes
 * AFTER `step` in the canonical path. Anchoring on the canonical path — rather
 * than the pending array's own offset — keeps forward navigation correct even
 * after the current step has been completed and dropped out of the pending
 * flow. (Deriving `next` from `flow.indexOf(step) + 1` breaks the moment the
 * current step is no longer pending: its index becomes -1 and the wizard jumps
 * past the real next step.) Returns null when nothing pending remains ahead.
 */
export function nextPendingStep(
  accountType: MorrowAccountType | null,
  input: OnboardingPendingInput,
  step: OnboardingStep,
): OnboardingStep | null {
  const path = stepsForAccountType(accountType);
  const flow = pendingOnboardingSteps(accountType, input);
  const currentIndex = Math.max(0, path.indexOf(step));
  return flow.find((candidate) => path.indexOf(candidate) > currentIndex) ?? null;
}

export function onboardingStepProgress(
  step: OnboardingStep,
  accountType: MorrowAccountType | null,
): { current: number; total: number } {
  const steps = stepsForAccountType(accountType);
  const index = steps.indexOf(step);
  return {
    current: index >= 0 ? index + 1 : 1,
    total: steps.length,
  };
}

export function organizationLogoUrl(organization: unknown): string {
  if (!organization || typeof organization !== "object") return "";
  const record = organization as Record<string, unknown>;
  if (record.hasImage !== true || typeof record.imageUrl !== "string") return "";
  return record.imageUrl.trim();
}

/** Onboarding company logo first, then Clerk org logo. */
export function resolveOrganizationLogoUrl(organization: unknown, localFallback = ""): string {
  const local = localFallback.trim();
  if (local) return local;
  return organizationLogoUrl(organization);
}
